import { useParams } from "react-router-dom";
import { PageShell } from "@/components/site/PageShell";
import { PageHeader } from "@/components/site/PageHeader";
import { Apartments } from "@/components/landing/Apartments";
import { ApartmentsCompare } from "@/components/landing/ApartmentsCompare";
import { AusstattungKomfort } from "@/components/landing/AusstattungKomfort";
import { ExteriorGallery } from "@/components/landing/ExteriorGallery";
import { InteriorGallery } from "@/components/landing/InteriorGallery";
import { FAQ } from "@/components/landing/FAQ";
import { ApartmentStickyBar } from "@/components/landing/ApartmentStickyBar";
import { Reveal } from "@/hooks/useReveal";
import { SeoHead } from "@/components/SeoHead";
import { APARTMENT_SEO, PAGE_SEO, resolveSeo } from "@/data/apartmentSeo";
import { useT, useLang } from "@/i18n/LanguageProvider";

/**
 * WohnungenPage – consolidated "apartments" sub-page for the redesigned
 * multi-page site. Also handles legacy SEO routes `/wohnung/:slug` so
 * social shares and pinned URLs continue to land here with the right
 * apartment focus (which Apartments.jsx auto-opens via focusApartmentId).
 */
export default function WohnungenPage() {
    const { slug } = useParams();
    const { lang } = useLang();
    const aptSeo = slug ? APARTMENT_SEO[slug] : null;
    const seo = resolveSeo(aptSeo || PAGE_SEO.wohnungen, lang);
    const focusApartmentId = aptSeo ? aptSeo.apartmentId : null;
    const t = useT();

    return (
        <PageShell testId="wohnungen-page">
            <SeoHead seo={seo} />
            <PageHeader
                eyebrow={t({ de: "Vier Eigentumswohnungen", en: "Four condominiums" })}
                title={t({ de: "Welche Wohnung passt", en: "Which apartment suits" })}
                titleAccent={t({ de: "zu Ihnen?", en: "you?" })}
                subtitle={t({
                    de: "Erdgeschoss · Obergeschoss · Penthouse · Loft-Maisonette – vier komplett unterschiedliche Wohnungen unter einem Dach. Mit Carport, Kellerraum und Effizienzhaus-40-Standard im Gesamtensemble G21.",
                    en: "Ground · Upper · Penthouse · Loft-Maisonette – four very different homes under one roof. With carport, storage and EH-40 standard across the entire G21 ensemble.",
                })}
                breadcrumb={[
                    { label: t({ de: "Start", en: "Home" }), to: "/" },
                    { label: t({ de: "Wohnungen", en: "Apartments" }) },
                ]}
                bg="mint"
                testId="wohnungen-header"
            />

            <Reveal><Apartments focusApartmentId={focusApartmentId} /></Reveal>
            <Reveal><ApartmentsCompare /></Reveal>
            <Reveal><AusstattungKomfort /></Reveal>
            <Reveal><ExteriorGallery /></Reveal>
            <Reveal><InteriorGallery /></Reveal>
            <Reveal><FAQ /></Reveal>
            <ApartmentStickyBar />
        </PageShell>
    );
}
